import { APIStatus } from "@/api/APIStatus.type";
import { deleteNotification } from "@/api/privateRequests/user/deleteNotification";
import { FetchPricesResponse } from "@/api/privateRequests/user/fetchPrices";
import { getUserNotifications } from "@/api/privateRequests/user/getUserNotifications";
import {
  deleteUsedFeature,
  postUsedFeature,
} from "@/api/privateRequests/user/postUsedFeature";
import {
  currentPathUnderLocale,
  detectBrowserContentLanguage,
} from "@/config/localePath";
import { trackSignupSuccess } from "@/events/googleAdsEvents";
import { withMixpanel } from "@/events/mixpanelClient";
import { mixpanelTrack } from "@/events/mixpanelTrack";
import { clearSignupContext } from "@/events/signupTracking";
import { reportDailyCreditTestGroup } from "@/experiments/dailyCreditExperiment";
import { reportSignupBonusTestGroup } from "@/experiments/signupBonusExperiment";
import { sendMessageToExtension } from "@/functions/sendMessageToExtension";
import {
  PayloadAction,
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import {
  type ContentLanguage,
  adoptProfileContentLanguage,
  getStoredContentLanguage,
  unlinkClub as postUnlinkClub,
} from "@repo/shared/api";
import type { MyClubOnboardingStep } from "@repo/shared/myClub";
import {
  type BillingProvider,
  cacheEntitlement,
  readCachedEntitlement,
  setCachedImportedClub,
} from "@repo/shared/redux";
import {
  fetchCredits,
  resetCredits,
  resetEvolvedPlayers,
} from "@repo/shared/redux";
import { fetchClubStats } from "@repo/shared/redux";
import { captureMessage } from "@sentry/react";
import { AxiosError } from "axios";
import {
  isAndroid,
  isIOS,
  isMobile,
  isMobileSafari,
} from "react-device-detect";

import {
  fetchPrices as _fetchPrices,
  getNumberOfPlayersInClub,
  getUser,
  logout,
  patchUser,
} from "../../api/privateApi";
import * as publicApi from "../../api/privateApi";
import {
  checkExtensionAvailability,
  syncExtensionStatusFromResponse,
} from "../clubImport/clubImportSlice";
import { showModal } from "../message/messageSlice";
import { IRootState } from "../store";

type BuyIntent = {
  subscriptionId: string;
  discountId?: string;
  discountCode?: string;
  promoCode?: string;
};

export type ImportDestination =
  | "club"
  | "transferList"
  | "duplicates"
  | "sbcStorage";

// title/message are the backend's English fallback; the card composes from the rest.
type UserNotificationBase = {
  id: string;
  title: string;
  message: string;
};

export type UserNotification =
  | (UserNotificationBase & {
      type: "IMPORT_UPDATED";
      destination: ImportDestination;
      added: number;
      removed: number;
    })
  | (UserNotificationBase & { type: "EVOLUTIONS_COMPLETED"; count: number })
  | (UserNotificationBase & { type: "ASK_REVIEW" });

export type UserNotificationType = UserNotification["type"];

export type ExtensionReviewStatus = "SHOWN" | "DISMISSED" | "REVIEWED";

export type Subscription = "free" | "silver" | "gold" | "diamond";

type SubscriberOnBoardingStep =
  | "import-club"
  | "solve-sbc-with-own-club"
  | "find-your-clubs-best-squad"
  | "see-best-chains-for-evos"
  | "find-hidden-profit-in-club"
  | "see-top-upgrades-for-active-squad";

export type User = {
  email: string;
  name: string;
  platform: Platform;
  paid: boolean;
  subscription: Subscription;
  uuid: string;
  playerCount: number;
  lastImportedAt: number;
  // Holds a club claim, so it can unlink (backend ADR 0030).
  clubLinked?: boolean;
  cancelsAt: number;
  usedFeatures?: UsedFeature[];
  allowTracking?: boolean;
  emailNotification?: boolean;
  canUploadTactic?: boolean;
  myClubNotifications?: boolean;
  isCoreTester?: boolean;
  firstTimeLogin?: boolean;
  showWelcomeBack?: boolean;
  usedIos?: boolean;
  usedAndroid?: boolean;
  completedOnboarding?: SubscriberOnBoardingStep[];
  feedbackPortalDisplayName?: string;
  subscriptionInterval?: "monthly" | "yearly";
  experimentGroups?: string[];
  evoBuilderTriesLeft?: number;
  evoBuilderLastResetAt?: string; // ISO timestamp of last reset
  customerId?: string;
  churnkeyHash?: string;
  extensionReviewStatus?: ExtensionReviewStatus;
  pastDue?: boolean;
  billingProvider?: BillingProvider;
  /**
   * The account holds a subscription bought under the pre-credits pricing. Set
   * by the backend; absent for everyone else. Combined with an active tier it
   * keeps the account on the old three-tier subscription page — see
   * showLegacySubPageSelector.
   */
  legacySubscriber?: boolean;
  favoriteClubs?: number[];
  favoriteCountries?: number[];
  /** Waitlisted features this account was let into, set by hand on the backend. */
  grantedAccess?: { objectives?: boolean };
  /** Content language mirrored from the picker (DEV-3572); EA locale tag. */
  language?: ContentLanguage;
  /** Last language auto-detected from this user's browser. Record only; `language` is the pick. */
  autoDetectedLanguage?: ContentLanguage;
};

export type Price = {
  id: string;
  subscription: string;
  monthlyPrice: number;
  yearlyPrice?: number;
  interval: string;
  savings?: number;
};

export type Platform = "Playstation" | "PC";

export type UsedFeature =
  | "BUILD_SQUADS"
  | "MARKED_EVO_COMPLETED"
  | "SOLVE_SBC"
  | "VIEW_EVO_PLAYER_WITH_FILTER"
  | "VIEW_PLAYER_WITH_FILTER"
  | "BEST_FORMATION"
  | "CLICKED_EVO_BUILDER_STEP"
  | "MY_CLUB_WELCOME"
  | MyClubOnboardingStep
  | "WELCOME_POPUP";

type UserState = {
  status: APIStatus;
  userNotifications: UserNotification[];
  testVariant?: string;
  user?: User;
  unsubscribeEmailStatus?: APIStatus;
  buyIntent?: BuyIntent;
  /**
   * In-app path to send the user to once login completes, set by whatever
   * opened the login modal.
   *
   * Same shape as `buyIntent` and for the same reason: the login modal is
   * raised through `showModal` from call sites that never render it, so there
   * is no callback to hand a destination to — it has to be parked somewhere
   * both ends can see. Consumed and cleared in LoginSection.
   */
  postLoginRedirect?: string;
  prices: Price[];
  currency: string;
  countryCode: string;
  /** Set to true when user explicitly logs in (not auto-login from localStorage) */
  didExplicitLogin: boolean;
};

const initialState: UserState = {
  status: "idle",
  user: {
    usedFeatures: [],
  } as User,
  userNotifications: [],
  prices: [],
  currency: "$",
  countryCode: "",
  didExplicitLogin: false,
};

type FetchUserResponse = {
  user: User;
  paid: boolean;
};

/** Request never got a response: aborted, offline, DNS/TLS. Not a 401. */
export const hasNoResponse = (error: unknown): boolean =>
  !!(error as AxiosError)?.isAxiosError && !(error as AxiosError).response;

// Fetched on mount, so it races the first seconds of a visit. One retry covers
// a dropped request; anything the server answered is rethrown untouched.
const RETRY_DELAY_MS = 1500;
const getUserWithRetry = async () => {
  try {
    return await getUser();
  } catch (error) {
    if (!hasNoResponse(error)) throw error;
    await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
    return getUser();
  }
};

export const fetchUser = createAsyncThunk(
  "user",
  async (
    { didLogin }: { didLogin: boolean },
    { dispatch },
  ): Promise<FetchUserResponse> => {
    try {
      const user = await getUserWithRetry();
      // The profile is the truth. It changes only from an explicit pick made
      // while logged out, claimed at login or when the account has none yet.
      // Otherwise every load follows the profile, prefixed URL included: a
      // stray /de link renders German until the user arrives, then loads
      // again under the profile's prefix. No loop: on that load the URL's
      // locale equals the profile. Anonymous visitors never redirect; the URL
      // alone decides their language.
      const local = getStoredContentLanguage();
      if (local && local !== user.language && (didLogin || !user.language)) {
        patchUser({ language: local }).catch((error) =>
          console.error("Failed to claim content language", error),
        );
      } else if (adoptProfileContentLanguage(user.language)) {
        // replace, not assign: Back must not land on the prefixed URL and
        // bounce here again.
        window.location.replace(currentPathUnderLocale(user.language));
      }
      // Recorded on every load, redirect or not; it never decides the page language.
      const detected = detectBrowserContentLanguage();
      if (detected && detected !== user.autoDetectedLanguage) {
        patchUser({ autoDetectedLanguage: detected }).catch((error) =>
          console.error("Failed to store detected language", error),
        );
      }
      const paid =
        user.subscription === "silver" ||
        user.subscription === "gold" ||
        user.subscription === "diamond";
      localStorage.setItem("loggedIn", "true");
      cacheEntitlement({
        paid,
        subscription: user.subscription,
        hasImportedClub: !!user.lastImportedAt,
        subscriptionInterval: user.subscriptionInterval,
      });
      localStorage.setItem("platform", user.platform);
      localStorage.setItem("uuid", user.uuid);

      dispatch(fetchUserNotifications());
      // Feature access is credit-based, so the balance and the current feature
      // prices are needed as soon as we know who the user is.
      dispatch(fetchCredits());

      // Identify BEFORE any track call so events attach to user.uuid, not the
      // anonymous $device id.
      withMixpanel((mp) => {
        mp.identify(user.uuid);
        mp.people.set({
          $email: user.email,
          $name: user.name,
          $platform: user.platform,
          $uuid: user.uuid,
          $subscription: user.subscription,
          $subscriptionInterval: user.subscriptionInterval,
        });
      });
      // After identify, before user_login, so that event already carries the
      // arms. Both are assigned by the backend on /profile, so they are known
      // now; a user is in at most one of the two credit tests.
      reportDailyCreditTestGroup(user.experimentGroups);
      reportSignupBonusTestGroup(user.experimentGroups);
      // Disabled for now.
      // mixpanelTrack("user_login");

      if (user.firstTimeLogin) {
        clearSignupContext();
        // The same signal the backend fires Mixpanel's user_signed_up on.
        trackSignupSuccess();
      }
      if (user.showWelcomeBack) {
        dispatch(
          showModal({
            modalType: "welcome",
            data: { showWelcomeBack: true },
          }),
        );
      }

      dispatch(checkExtensionAvailability({ user }));

      if (isIOS && !user.usedIos) {
        dispatch(updateUser({ usedIos: true }));
      }
      if (isAndroid && !user.usedAndroid) {
        dispatch(updateUser({ usedAndroid: true }));
      }

      return {
        user,
        paid,
      };
    } catch (error) {
      // No logout, whatever failed: only a rejected credential ends a session
      // and the 401 interceptor owns that. See commit message.
      // No response is reported once by the response interceptor.
      if (hasNoResponse(error)) return;
      captureMessage("User fetch failed", {
        level: "error",
        tags: { error_type: "user_fetch_failed" },
        extra: { error: error },
      });

      return;
    }
  },
);

export const markNotificationAsRead = createAsyncThunk(
  "mark-notification-as-read",
  async ({ notificationId }: { notificationId: string }) => {
    deleteNotification({ notificationId });
  },
);

/**
 * Ticks a My Club onboarding step complete, or unticks it. Shown at once,
 * and put back if the account doesn't take the change.
 */
export const setOnboardingStep = createAsyncThunk(
  "set-onboarding-step",
  async (
    { step, done }: { step: MyClubOnboardingStep; done: boolean },
    { dispatch },
  ) => {
    dispatch(done ? addUsedFeature(step) : removeUsedFeature(step));
    try {
      await (done ? postUsedFeature(step) : deleteUsedFeature(step));
    } catch {
      dispatch(done ? removeUsedFeature(step) : addUsedFeature(step));
    }
  },
);

export const usedFeature = createAsyncThunk(
  "used-feautre",
  async ({ feature }: { feature: UsedFeature }, { getState, dispatch }) => {
    const { userReducer } = getState() as IRootState;
    // is logged in and the feature has not been used yet
    if (
      userReducer.user?.uuid &&
      !userReducer.user?.usedFeatures?.includes(feature)
    ) {
      await postUsedFeature(feature);
      // for some reason, if it fails, it will not update the user state which is fine.
      dispatch(
        setUpdateUser({
          usedFeatures: [...(userReducer.user?.usedFeatures || []), feature],
        }),
      );
    }
  },
);

export const fetchUserNotifications = createAsyncThunk(
  "user-notifications",
  async () => getUserNotifications(),
);

export const logoutUser = createAsyncThunk(
  "logout-user",
  async (_, { dispatch }) => {
    // Storage and Mixpanel teardown lives in endUserSession, which `logout`
    // calls — the forced-logout path (expired token, 401) has to leave the same
    // browser behind, and it never reaches this thunk. Only the redux state
    // below is this thunk's own business.
    await logout();
    dispatch(resetCredits());
    // My Evolutions' lists belong to the account.
    dispatch(resetEvolvedPlayers());
    return;
  },
);

export const unsubscribeEmail = createAsyncThunk(
  "unsubscribe-email",
  async ({ userId, cioId }: { userId: string; cioId: string }) => {
    return await publicApi.unsubscribeEmail(userId, cioId);
  },
);

export const updateUser = createAsyncThunk(
  "update-user",
  async (data: any, { getState, dispatch }) => {
    const state = getState() as IRootState;
    const user = state.userReducer.user;

    if (data["platform"]) {
      localStorage.setItem("platform", data["platform"]);
    }
    if (
      data["allowTracking"] !== undefined &&
      ["gold", "diamond"].includes(user.subscription)
    ) {
      const { browser } = state.clubImportReducer;
      const safariMobile = browser === "iosSafari" || isMobileSafari;
      sendMessageToExtension({
        user: { ...user, allowTracking: data["allowTracking"] },
        safariMobile,
      }).then((res) => {
        dispatch(syncExtensionStatusFromResponse(res));
      });
    }
    const { completedOnboarding, ...rest } = data ?? {};
    if (Object.keys(rest).length) {
      await patchUser(rest);
    }
    dispatch(setUpdateUser(data));
  },
);

export const fetchPlayerCount = createAsyncThunk(
  "fetch-player-count",
  async (_, { getState, dispatch }) => {
    const state = getState() as IRootState;
    const user = state.userReducer.user;
    const playerCount = await getNumberOfPlayersInClub(user.uuid);
    if (playerCount.playerCount > 0) {
      // A first import completing mid-session: record it now so the next page
      // load resolves club-data access from the cache instead of waiting on
      // users/profile.
      setCachedImportedClub(true);
    }
    dispatch(
      setUpdateUser({
        playerCount: playerCount.playerCount,
        ...(playerCount.playerCount > 0 ? { lastImportedAt: Date.now() } : {}),
      }),
    );
    return playerCount;
  },
);

// Imports and unlinks change these, and the club-import gate reads them.
export const fetchClubImportState = createAsyncThunk(
  "fetch-club-import-state",
  async (_, { dispatch }) => {
    const { clubLinked, lastImportedAt, playerCount } = await getUser();
    setCachedImportedClub(!!lastImportedAt);
    dispatch(setUpdateUser({ clubLinked, lastImportedAt, playerCount }));
  },
);

export const unlinkClub = createAsyncThunk(
  "unlink-club",
  async (_, { dispatch }) => {
    await postUnlinkClub();
    await Promise.all([
      dispatch(fetchClubImportState()),
      dispatch(fetchClubStats()),
    ]);
  },
);

export const fetchPrices = createAsyncThunk("fetch-prices", async () => {
  const prices = await _fetchPrices();
  return prices;
});

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    removeUsedFeature(state, action: PayloadAction<UsedFeature>) {
      state.user.usedFeatures = state.user.usedFeatures?.filter(
        (feature) => feature !== action.payload,
      );
    },
    /** A used feature recorded on the account, shown before a refetch. */
    addUsedFeature(state, action: PayloadAction<UsedFeature>) {
      if (!state.user.usedFeatures?.includes(action.payload)) {
        state.user.usedFeatures = [
          action.payload,
          ...(state.user.usedFeatures || []),
        ];
      }
    },
    setTestVariant(state, action) {
      state.testVariant = action.payload;
    },
    setBuyIntent(state, action: PayloadAction<BuyIntent>) {
      state.buyIntent = action.payload;
    },
    setUpdateUser(state, action: PayloadAction<Partial<User>>) {
      state.user = { ...state.user, ...action.payload };
    },
    clearBuyIntent(state) {
      state.buyIntent = undefined;
    },
    /** Park an in-app path for LoginSection to navigate to once login lands. */
    setPostLoginRedirect(state, action: PayloadAction<string>) {
      state.postLoginRedirect = action.payload;
    },
    clearPostLoginRedirect(state) {
      state.postLoginRedirect = undefined;
    },
    clearDidExplicitLogin(state) {
      state.didExplicitLogin = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUser.pending, (state) => {
        state.status = "pending";
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.status = "fulfilled";
        if (action.payload) {
          state.user = action.payload.user;
          state.user.paid = action.payload.paid;

          // Track if this was an explicit login (not auto-login from localStorage)
          if (action.meta.arg.didLogin) {
            state.didExplicitLogin = true;
          }
        } else {
          state.status = "rejected";
        }
      })
      .addCase(fetchUser.rejected, (state) => {
        state.status = "rejected";
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.status = "fulfilled";
        state.user = {} as User;
      })
      .addCase(unsubscribeEmail.pending, (state) => {
        state.unsubscribeEmailStatus = "pending";
      })
      .addCase(unsubscribeEmail.fulfilled, (state) => {
        state.unsubscribeEmailStatus = "fulfilled";
      })
      .addCase(unsubscribeEmail.rejected, (state) => {
        state.unsubscribeEmailStatus = "rejected";
      })
      .addCase(fetchUserNotifications.fulfilled, (state, action) => {
        state.userNotifications = action.payload;
      })
      .addCase(markNotificationAsRead.pending, (state, action) => {
        const notificationId = action.meta.arg.notificationId;
        state.userNotifications = state.userNotifications?.filter(
          (notification) => notification && notification.id !== notificationId,
        );
      })
      .addCase(usedFeature.fulfilled, (state, action) => {
        state.user.usedFeatures = [
          action.meta.arg.feature,
          ...(state.user.usedFeatures || []),
        ];
      })
      .addCase(updateUser.fulfilled, (state) => {
        state.status = "fulfilled";
      })
      .addCase(updateUser.rejected, (state) => {
        state.status = "rejected";
      })
      .addCase(
        fetchPrices.fulfilled,
        (state, action: PayloadAction<FetchPricesResponse>) => {
          state.prices = action.payload.prices;
          state.currency = action.payload.currency;
          state.countryCode = action.payload.countryCode;
        },
      );
  },
});

export const {
  setTestVariant,
  setBuyIntent,
  clearBuyIntent,
  setPostLoginRedirect,
  clearPostLoginRedirect,
  setUpdateUser,
  clearDidExplicitLogin,
  addUsedFeature,
  removeUsedFeature,
} = userSlice.actions;

export const postLoginRedirectSelector = (state: IRootState) =>
  state.userReducer.postLoginRedirect;

export const getUserSelector = (state: IRootState) => state.userReducer;

export const userIsLoggedInSelector = (state: IRootState) =>
  !!state.userReducer.user.uuid || !!localStorage.getItem("access_token");

export const didExplicitLoginSelector = (state: IRootState) =>
  state.userReducer.didExplicitLogin;

export const userIsPaidSelector = (state: IRootState) =>
  state.userReducer.user.paid;

export const userSubscriptionSelector = (state: IRootState) =>
  readCachedEntitlement().subscription || state.userReducer.user.subscription;

/*
  Transitional. Club import is open to everyone under the credits model, so this
  tier check has no place in the new one — its call sites split into club-data
  features (gated on having imported, see permissions/featureCatalog.ts) and the
  import action itself (login only). Delete with the enforcement migration.

  The cold-start fallback no longer reads a dedicated key: the old
  `canUseClubImport` entry was written from this same gold/diamond check, so the
  cached subscription already answers it.
*/
export const canUseClubImportSelector = (state: IRootState) =>
  !!state.userReducer.user.uuid || !!localStorage.getItem("access_token");

/*
  Ads moved to redux/permissions/entitlements.ts (useShowAds). The selector that
  lived here read `user.paid` with no cold-start fallback, so a returning
  subscriber rendered empty ad slots until users/profile resolved — while
  App.tsx, which did read the cache, correctly skipped loading the ad script.
  One entitlement selector now feeds every consumer.
*/

export const hasPlayersImportedSelector = (state: IRootState) =>
  state.userReducer.user.playerCount > 0;
/**
 * Signed in with cards in the club: the one rule behind every club-aware
 * SBC solve on the web. The backend then runs the club pools, the solve
 * draws a Credit, and swaps offer club cards.
 */
export const solvesWithClubSelector = (state: IRootState) =>
  Boolean(state.userReducer.user?.uuid) &&
  state.userReducer.user.playerCount > 0;

/**
 * Zero players in the club. `undefined` (profile not loaded yet) is
 * deliberately not "empty": acting on it would flash import prompts at users
 * whose club is fine.
 */
export const clubIsEmptySelector = (state: IRootState) =>
  state.userReducer.user?.playerCount === 0;

/**
 * The club was imported at least once, however long ago. The single answer to
 * "is this a first import or a repeat?", which decides copy, CTAs and where the
 * import flow routes.
 *
 * `lastImportedAt` only arrives with users/profile, so the cached flag covers
 * the window after a page load — without it a returning importer briefly reads
 * as a first-timer and gets first-import copy that then swaps under them. The
 * cache is read per call so it follows a logout clearing it.
 */
export const hasImportedBeforeSelector = (state: IRootState) =>
  !!state.userReducer.user?.lastImportedAt ||
  readCachedEntitlement().hasImportedClub;

/**
 * Show the pre-credits, three-tier subscription page instead of the single-plan
 * one. Only for a legacy subscriber whose old-model subscription is still
 * active: once the tier lapses to free there is nothing left to manage on that
 * page, and the account buys credits like everyone else.
 */
export const showLegacySubPageSelector = (state: IRootState) =>
  !!state.userReducer.user?.legacySubscriber && hasSubSelector(state);

/** Whether users/profile has answered yet, for callers that must not guess. */
export const userProfileResolvedSelector = (state: IRootState) =>
  state.userReducer.status === "fulfilled" ||
  state.userReducer.status === "rejected";

/**
 * Whether users/profile actually answered — fulfilled, not merely finished.
 *
 * `rejected` covers a 500, a dropped connection and a timeout as much as a
 * 401, so it means "we could not find out", not "there is no account". A
 * caller that treats the two alike will read a signed-in user as signed out
 * on any blip, while their token sits in localStorage and keeps
 * authenticating their requests.
 */
export const userProfileAnsweredSelector = (state: IRootState) =>
  state.userReducer.status === "fulfilled";

export const buyIntentSelector = (state: IRootState) =>
  state.userReducer.buyIntent;

export const userPricesSelector = (state: IRootState) =>
  state.userReducer.prices;

export const userCurrencySelector = (state: IRootState) =>
  state.userReducer.currency;

export const hasSubSelector = (state: IRootState) =>
  state.userReducer.user?.subscription === "silver" ||
  state.userReducer.user?.subscription === "gold" ||
  state.userReducer.user?.subscription === "diamond";

/*
  Multi-club favorites. Memoized so the returned array keeps a stable reference
  across renders — a fresh `[]` each call would retrigger effects that depend on
  it (infinite refetch loop).
*/
export const userFavoriteClubIdsSelector = createSelector(
  [(state: IRootState) => state.userReducer.user?.favoriteClubs],
  (clubs): number[] => clubs ?? [],
);

// UI supports a single favorite country; backend stores it as an array.
export const userFavoriteCountryIdSelector = (state: IRootState) =>
  state.userReducer.user?.favoriteCountries?.[0];

export default userSlice.reducer;
